import { getHTML, getTextWithEntityPlaceholders } from '@amzn/awscat-editor';
import {
  ContentMetadata,
  ContentNode,
  ContentNodeType,
  ContentType,
  WorkflowSteps,
  WorkflowStepsCurrentStateLabel,
  BarRaiserStatus,
  ContentAudience,
  UserType,
  UserProfile,
  UserRoles,
  ContentWorkflow,
  WorkflowState,
  Content,
  CodeRepoReviewStatus,
} from '@amzn/awscat-pattern-library-service-model';
import { paramCase } from 'change-case';
import sanitizeHtml from 'sanitize-html';
import { determineProgress } from './WorkflowProgressUtil';
import { deepClone } from './UtilsApi';

// Helper method to tranform Froala HTML into Tiptap HTML schema
export const transformTiptapHtml = (html: string) => {
  const transformed = sanitizeHtml(html, {
    allowedTags: false,
    allowedAttributes: false,
    transformTags: {
      span: (tagName: any, attribs?: any) => {
        if (attribs?.class === 'fr-class-code') {
          return { tagName: 'code', attribs: {} };
        } else {
          return { tagName, attribs };
        }
      },
    },
  });
  return transformed;
};

export enum NodeAttribute {
  id = 'id',
  href = 'href',
}

const patternHrefMap = new Map<string, string>([
  ['summary', 'summary'],
  ['assumption', 'prereqs'],
  ['architecture', 'architecture'],
  ['best-practices', 'best-practices'],
  ['epics', 'epics'],
  ['troubleshooting', 'troubleshooting'],
  ['tools', 'tools'],
  ['references', 'resources'],
  ['miscellaneous', 'additional'],
]);

export const findNodeById = (nodeId: string, contentNodes: ContentNode[]) => {
  return findNode(nodeId, contentNodes, NodeAttribute.id);
};

export const findNodeByHref = (href: string, contentNodes: ContentNode[]) => {
  return findNode(href, contentNodes, NodeAttribute.href);
};

const findNode = (
  value: string,
  contentNodes: ContentNode[],
  attribute: NodeAttribute = NodeAttribute.id,
): ContentNode => {
  if (!value || !Array.isArray(contentNodes) || contentNodes.length === 0) {
    // @ts-expect-error TS2322 - strict migration
    return null;
  }

  for (const contentNode of contentNodes) {
    if (contentNode[attribute] === value) {
      return contentNode;
    }
    const subNode =
      findNode(value, contentNode.contentSections, attribute) ||
      findNode(value, contentNode.subContent, attribute);

    if (subNode) {
      return subNode;
    }
  }
  // @ts-expect-error TS2322 - strict migration
  return null;
};

export const findParentNode = (
  nodeId: string,
  curNode: ContentNode,
  parentNode: ContentNode,
): ContentNode => {
  if (!curNode) {
    // @ts-expect-error TS2322 - strict migration
    return null;
  }

  if (curNode.id === nodeId) {
    return parentNode;
  } else {
    for (const subContent of curNode.subContent) {
      const parent = findParentNode(nodeId, subContent, curNode);
      if (parent) {
        return parent;
      }
    }
    for (const section of curNode.contentSections) {
      const parent = findParentNode(nodeId, section, curNode);
      if (parent) {
        return parent;
      }
    }
  }
  // @ts-expect-error TS2322 - strict migration
  return null;
};

export const getPageNestingDepth = (node: ContentNode, level: number = 0): number => {
  if (!node.subContent) {
    return 0;
  }

  let maxChildDepth = level;
  for (const child of node.subContent) {
    const curChildDepth = getPageNestingDepth(child, level + 1);
    maxChildDepth = Math.max(maxChildDepth, curChildDepth);
  }
  return maxChildDepth;
};

export const autoGenerateHref = (
  node: ContentNode,
  contentMetadata: ContentMetadata,
  alwaysGenerate: boolean = false,
) => {
  if (!node) {
    return;
  }
  if (alwaysGenerate || !node.href) {
    // @ts-expect-error TS2322 - strict migration
    node.href = getNodeHref(node, contentMetadata);
  }

  for (const subContent of node.subContent) {
    autoGenerateHref(subContent, contentMetadata, alwaysGenerate);
  }
  for (const section of node.contentSections) {
    autoGenerateHref(section, contentMetadata, alwaysGenerate);
  }
};

// Generate href value for the current node
export const getNodeHref = (currentNode: ContentNode, contentMetadata: ContentMetadata) => {
  const { contentType, contentName, contentShortName } = contentMetadata;
  const { title, nodeType, href: contentNodeHref } = currentNode;

  // Return node href if exist, otherwise auto-generate one
  if (contentNodeHref) {
    return contentNodeHref;
  }

  let href;
  switch (contentType) {
    case ContentType.Pattern:
      // TODO: clarify with product if we need a default pathslug for patterns
      href = patternHrefMap.get(currentNode.id) || paramCase(getTextWithEntityPlaceholders(title));
      break;
    case ContentType.Guide:
      href =
        nodeType === ContentNodeType.Document
          ? paramCase(contentShortName || contentName)
          : paramCase(getTextWithEntityPlaceholders(title));
      break;
    case ContentType.Strategy:
      href =
        nodeType === ContentNodeType.Document
          ? `strategy-${paramCase(contentShortName || contentName)}`
          : paramCase(getTextWithEntityPlaceholders(title));
      break;
    default:
      break;
  }
  return href;
};

export const guideOrStrategy = (contentType: ContentType) =>
  [ContentType.Guide, ContentType.Strategy].includes(contentType);

export const userTypeToAudienceMap = new Map<UserType, ContentAudience[]>([
  [UserType.AWS, [ContentAudience.PUBLIC, ContentAudience.EXTERNAL, ContentAudience.INTERNAL]],
  [UserType.Partner, [ContentAudience.PUBLIC, ContentAudience.EXTERNAL, ContentAudience.PARTNER]],
]);

export const isAllowedToEditPost = (userProfile: UserProfile) => {
  if (!userProfile) {
    return false;
  }
  const allowedEditingRoles = [UserRoles.Admin, UserRoles.TechWriter];
  // @ts-expect-error TS2769 - strict migration
  return Array.from(userProfile.roles).some((r) => allowedEditingRoles.includes(r));
};

export const postFilter = (workflows: ContentWorkflow[]) =>
  workflows.filter((wf) => wf.draftContentMetadata.contentType === ContentType.Post);

export const pgsFilter = (workflows: ContentWorkflow[]) =>
  workflows.filter((wf) =>
    [ContentType.Pattern, ContentType.Guide, ContentType.Strategy].includes(
      wf.draftContentMetadata.contentType,
    ),
  );

export const supportsCodeRepos = (contentType: ContentType) =>
  [ContentType.Pattern, ContentType.Guide].includes(contentType);

export const DRAFT_STATES = [
  WorkflowState.NEW,
  WorkflowState.MODIFIED,
  WorkflowState.UNMODIFIED,
  WorkflowState.REJECTED,
  WorkflowState.UNPUBLISHED,
];

export const PUBLISHED_STATES = [
  WorkflowState.PUBLISHED,
  WorkflowState.FINALIZED,
  WorkflowState.FINALIZING,
  WorkflowState.REVOKED,
  WorkflowState.DISTRIBUTED,
];

// Concat different content data format (string | JSON object) into a single string
export const concatContentData = (...args: Array<string | object>): string => {
  return args.reduce((acc: string, cur: string | object) => {
    if (typeof cur === 'string') {
      acc += cur;
    } else {
      acc += getHTML(cur) as string;
    }
    return acc;
  }, '') as string;
};

export const isContentInTechWriterReview = (contentWorkflow: ContentWorkflow) => {
  const progress = determineProgress(contentWorkflow);
  return progress === WorkflowStepsCurrentStateLabel.get(WorkflowSteps.TechwriterReview);
};

export const isUnpublishRecommended = (workflow?: ContentWorkflow): boolean => {
  return (
    workflow?.publicationWorkflows?.[0]?.barRaiserStatus === BarRaiserStatus.UnpublishingRecommended
  );
};

export const isInBarRaiserPublishedReview = (workflow?: ContentWorkflow): boolean => {
  return (
    workflow?.draftState === WorkflowState.UNMODIFIED &&
    // A BR evaluation can score 0, so check presence rather than truthiness (D541096198).
    (workflow?.lastQualityScore === undefined || isUnpublishRecommended(workflow))
  );
};

export const isWorkflowReviewedByCurrentUser = (
  userId: string,
  workflow?: ContentWorkflow,
): boolean => {
  return (
    !!workflow?.publicationWorkflows?.length &&
    isUnpublishRecommended(workflow) &&
    workflow?.publicationWorkflows[0]?.barRaiserId === userId
  );
};

export const resetOpenSourceCodeReview = (contentAudience: ContentAudience, content: Content) => {
  const { contentTypeMetadata } = content;
  const { codeLinks } = contentTypeMetadata;
  const hasCodeLinks = Array.isArray(codeLinks) && codeLinks.length > 0;
  if (contentAudience === ContentAudience.INTERNAL && hasCodeLinks) {
    const updatedCodeLinks = deepClone(codeLinks);
    updatedCodeLinks[0].openSourceTicket = undefined;
    updatedCodeLinks[0].openSourceStatus = CodeRepoReviewStatus.NotStarted;
    return {
      ...content,
      contentTypeMetadata: { ...contentTypeMetadata, codeLinks: updatedCodeLinks },
    };
  } else {
    return content;
  }
};

/**
 * Replaces non-breaking space characters (&nbsp;) with regular space characters
 * in the provided string.
 * @param text - The input string containing &nbsp; characters to be replaced
 * @returns The string with all &nbsp; characters converted to regular spaces
 */
export const replaceNBSPWithSpace = (text: string) => {
  return text?.replace(/\u00A0/g, ' ');
};
